import * as React from "react"
import { Slot } from "radix-ui"

import { cn } from "../../../lib/utils"
import { Icon } from "../icon/icon"
import type { IconName } from "../icon/icon-registry"

/**
 * Badge / chip — DESIGN.md `chip-*` + `badge-status-*`.
 *
 * All variants use 6px radius (`rounded-sm`) per DESIGN.md.
 * Status variants render an inline dot indicator when `dot` is set;
 * `status-connected` defaults to a dot via the component logic below.
 */

type BadgeVariant =
  | "default"             // chip-default
  | "ai"                  // chip-ai — AI Mint at 15% opacity
  | "slate"               // chip-slate — slate metadata chip
  | "status-connected"    // emerald-50 / emerald-700, dot indicator
  | "status-sent"         // blue-50 / blue-700
  | "status-in-production" // amber-50 / amber-700
  | "status-delivered"    // emerald-50 / emerald-700 (no dot)
  | "status-error"        // red-50 / red-700 — subtle error status
  // Legacy — kept as deprecated aliases through this milestone
  | "secondary"
  | "destructive"
  | "outline"
  | "ghost"
  | "link"

const baseBadgeClass =
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-sm border border-transparent px-1.5 py-0.5 text-[10px] font-normal leading-none whitespace-nowrap transition-colors focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40 aria-invalid:border-error aria-invalid:ring-error/20 [&>svg]:pointer-events-none [&>svg]:size-3"

const badgeVariantClasses: Record<BadgeVariant, string> = {
  default: "bg-muted text-foreground",
  ai: "bg-[var(--ai-fill-chip)] text-[var(--ai-text)]",
  slate: "bg-[var(--slate-chip-bg)] text-[var(--slate-chip-text)]",
  "status-connected": "bg-success-subtle text-success-subtle-foreground",
  "status-sent": "bg-info-subtle text-info-subtle-foreground",
  "status-in-production": "bg-warning-subtle text-warning-subtle-foreground",
  "status-delivered": "bg-success-subtle text-success-subtle-foreground",
  "status-error": "bg-error-subtle text-error-subtle-foreground",
  // Legacy aliases (TODO(cleanup): migrate consumers off these)
  secondary: "bg-muted text-foreground",
  destructive: "bg-error text-error-foreground",
  outline: "border-border text-foreground hover:bg-muted",
  ghost: "text-foreground hover:bg-muted",
  link: "text-primary underline-offset-4 hover:underline",
}

function badgeVariants({ variant = "default" }: { variant?: BadgeVariant } = {}) {
  return cn(baseBadgeClass, badgeVariantClasses[variant])
}

const variantsWithDefaultDot: ReadonlyArray<BadgeVariant> = ["status-connected"]

function Badge({
  className,
  variant = "default",
  asChild = false,
  iconStart,
  iconEnd,
  dot,
  children,
  ...props
}: React.ComponentProps<"span"> & {
  variant?: BadgeVariant
  asChild?: boolean
  iconStart?: IconName
  iconEnd?: IconName
  /** Render a colored dot indicator before children. Defaults to `true` for `status-connected`. */
  dot?: boolean
}) {
  const Comp = asChild ? Slot.Root : "span"
  const showDot = dot ?? variantsWithDefaultDot.includes(variant)

  const dotColor =
    variant === "status-connected" || variant === "status-delivered"
      ? "bg-success"
      : variant === "status-sent"
        ? "bg-info"
        : variant === "status-in-production"
          ? "bg-warning"
          : variant === "status-error"
            ? "bg-error"
            : "bg-foreground"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {showDot ? (
            <span
              aria-hidden="true"
              className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full", dotColor)}
            />
          ) : null}
          {iconStart ? <Icon name={iconStart} color="inherit" size="xs" /> : null}
          {children}
          {iconEnd ? <Icon name={iconEnd} color="inherit" size="xs" /> : null}
        </>
      )}
    </Comp>
  )
}

export { Badge, badgeVariants }
